/**
 * useClientResourceMap Hook
 * Manages ClientResourceMap data and operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchClientResourceMaps,
  fetchEnabledClientResourceMaps,
  fetchClientResourceMap,
  fetchClientResourceMapsByResource,
  createClientResourceMap,
  updateClientResourceMap,
  batchUpdateClientResourceMapOrder,
  deleteClientResourceMap,
} from '../services/resourceService';

/**
 * Custom hook for managing client resource map data and operations
 * @param {string} clientId - Client ID (required)
 * @param {Object} options - Hook options
 * @param {Object} options.filter - Optional filter criteria
 * @param {boolean} options.enabledOnly - Whether to fetch only enabled resources (default: false)
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Client resource map data, mutations, loading states, and utility functions
 */
export function useClientResourceMap(clientId, options = {}) {
  const { filter = null, enabledOnly = false, enabled = true } = options;
  const queryClient = useQueryClient();

  // Determine which fetch function to use
  const fetchFunction = () => {
    if (enabledOnly) {
      return fetchEnabledClientResourceMaps(clientId);
    }
    return fetchClientResourceMaps(clientId, filter);
  };

  // Fetch client resource maps
  const {
    data: resourceMaps = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clientResourceMaps', clientId, filter, enabledOnly],
    queryFn: fetchFunction,
    enabled: enabled && !!clientId,
  });

  // Create client resource map mutation
  const createMutation = useMutation({
    mutationFn: createClientResourceMap,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientResourceMaps', clientId] });
    },
  });

  // Update client resource map mutation
  const updateMutation = useMutation({
    mutationFn: updateClientResourceMap,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientResourceMaps', clientId] });
    },
  });

  // Batch update order mutation
  const batchUpdateOrderMutation = useMutation({
    mutationFn: batchUpdateClientResourceMapOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientResourceMaps', clientId] });
    },
  });

  // Delete client resource map mutation
  const deleteMutation = useMutation({
    mutationFn: deleteClientResourceMap,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientResourceMaps', clientId] });
    },
  });

  // Utility functions
  const getEnabledResources = () => resourceMaps.filter((rm) => rm.isEnabled === true);

  const getDisabledResources = () => resourceMaps.filter((rm) => rm.isEnabled === false);

  const getResourceMapByResourceId = (resourceId) =>
    resourceMaps.find((rm) => rm.resourceId === resourceId);

  const sortByOrder = (maps = resourceMaps) =>
    [...maps].sort((a, b) => (a.order || 0) - (b.order || 0));

  const searchResourceMaps = (searchTerm) => {
    if (!searchTerm) return resourceMaps;
    const searchLower = searchTerm.toLowerCase();
    return resourceMaps.filter((rm) =>
      rm.customLabel?.toLowerCase().includes(searchLower) ||
      rm.customDescription?.toLowerCase().includes(searchLower)
    );
  };

  /**
   * Reorders resource maps and updates the backend
   * @param {Array<{id: string, order: number}>} newOrder - Array of resource maps with new order values
   * @returns {Promise} Promise that resolves when updates complete
   */
  const reorderResources = async (newOrder) => {
    // Optimistically update the cache
    queryClient.setQueryData(['clientResourceMaps', clientId], (old) => {
      const updated = [...(old || [])];
      newOrder.forEach(({ id, order }) => {
        const index = updated.findIndex((rm) => rm.id === id);
        if (index !== -1) {
          updated[index] = { ...updated[index], order };
        }
      });
      return updated.sort((a, b) => a.order - b.order);
    });

    // Perform the actual update
    try {
      await batchUpdateOrderMutation.mutateAsync(newOrder);
    } catch (error) {
      // Revert optimistic update on error
      queryClient.invalidateQueries({ queryKey: ['clientResourceMaps', clientId] });
      throw error;
    }
  };

  /**
   * Toggles the enabled status of a resource map
   * @param {string} id - Resource map ID
   * @param {boolean} isEnabled - New enabled status
   * @returns {Promise} Promise that resolves when update completes
   */
  const toggleEnabled = async (id, isEnabled) => {
    return updateMutation.mutateAsync({ id, isEnabled });
  };

  /**
   * Updates customization fields for a resource map (CLIENT_ADMIN safe)
   * @param {string} id - Resource map ID
   * @param {Object} customization - Customization fields
   * @param {string} customization.customLabel - Custom label
   * @param {string} customization.customTooltip - Custom tooltip
   * @param {string} customization.customDescription - Custom description
   * @param {string} customization.customIcon - Custom icon
   * @param {number} customization.order - Display order
   * @returns {Promise} Promise that resolves when update completes
   */
  const updateCustomization = async (id, customization) => {
    const allowedFields = ['customLabel', 'customTooltip', 'customDescription', 'customIcon', 'order'];
    const sanitizedInput = { id };

    // Only include allowed fields
    allowedFields.forEach((field) => {
      if (customization[field] !== undefined) {
        sanitizedInput[field] = customization[field];
      }
    });

    return updateMutation.mutateAsync(sanitizedInput);
  };

  /**
   * Gets the next available order value
   * @returns {number} Next order value
   */
  const getNextOrderValue = () => {
    if (resourceMaps.length === 0) return 1;
    const maxOrder = Math.max(...resourceMaps.map((rm) => rm.order || 0));
    return maxOrder + 1;
  };

  return {
    // Data
    resourceMaps,
    isLoading,
    error,

    // Mutations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    batchUpdateOrder: batchUpdateOrderMutation.mutate,
    batchUpdateOrderAsync: batchUpdateOrderMutation.mutateAsync,
    remove: deleteMutation.mutate,
    removeAsync: deleteMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isBatchUpdating: batchUpdateOrderMutation.isPending,
    isDeleting: deleteMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    batchUpdateError: batchUpdateOrderMutation.error,
    deleteError: deleteMutation.error,

    // Utility functions
    refetch,
    getEnabledResources,
    getDisabledResources,
    getResourceMapByResourceId,
    sortByOrder,
    searchResourceMaps,
    reorderResources,
    toggleEnabled,
    updateCustomization,
    getNextOrderValue,
  };
}

/**
 * Custom hook for fetching a single client resource map
 * @param {string} id - Client resource map ID
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Client resource map data and loading state
 */
export function useClientResourceMapEntry(id, options = {}) {
  const { enabled = true } = options;

  const { data: resourceMap, isLoading, error } = useQuery({
    queryKey: ['clientResourceMap', id],
    queryFn: () => fetchClientResourceMap(id),
    enabled: enabled && !!id,
  });

  return {
    resourceMap,
    isLoading,
    error,
  };
}

/**
 * Custom hook for fetching client resource maps by resource ID
 * @param {string} resourceId - Resource ID
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Client resource maps data filtered by resource
 */
export function useClientResourceMapsByResource(resourceId, options = {}) {
  const { enabled = true } = options;

  const { data: resourceMaps = [], isLoading, error, refetch } = useQuery({
    queryKey: ['clientResourceMapsByResource', resourceId],
    queryFn: () => fetchClientResourceMapsByResource(resourceId),
    enabled: enabled && !!resourceId,
  });

  return {
    resourceMaps,
    isLoading,
    error,
    refetch,
  };
}
